import React from "react";
import { FormattedMessage } from "react-intl";

import { cn } from "~/common/redesign-v1";
import { ChevronDown } from "~/common/redesign-v1/icons";

export function RdTable({
  children,
  className,
  containerClassName,
  noScroll = false,
  scrollX = false,
}: {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  noScroll?: boolean;
  scrollX?: boolean;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = React.useState({ left: 0, width: 100 });

  React.useEffect(() => {
    if (!scrollX) return;
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      if (scrollWidth <= clientWidth) {
        setThumb({ left: 0, width: 100 });
        return;
      }
      setThumb({ left: (scrollLeft / scrollWidth) * 100, width: (clientWidth / scrollWidth) * 100 });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollX, children]);

  return (
    <div className={cn("tw-w-full", scrollX && "tw-flex tw-flex-col tw-gap-1.5")}>
      <div
        ref={scrollRef}
        data-scroll-x={scrollX ? "" : undefined}
        className={cn("tw-w-full tw-rounded-rd tw-border tw-border-solid tw-border-border tw-bg-card", noScroll ? "tw-overflow-x-hidden" : "tw-overflow-x-auto", containerClassName)}
      >
        <table className={cn("tw-w-full tw-border-collapse tw-text-left tw-text-sm", className)}>{children}</table>
      </div>
      {scrollX ? (
        <div className="tw-h-1.5 tw-w-full tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-muted" aria-hidden="true">
          <div className="tw-h-full tw-rounded-full tw-bg-muted-foreground" style={{ width: `${thumb.width}%`, marginInlineStart: `${thumb.left}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export type SortState = { key: string; direction: "asc" | "desc" } | null;

export function useTableSort<Row>(rows: Row[], accessors: Record<string, (row: Row) => string | number | null | undefined>, initial: SortState = null) {
  const [sortState, setSortState] = React.useState<SortState>(initial);
  const toggle = React.useCallback((key: string) => setSortState((prev) => (prev?.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" })), []);
  const sorted = React.useMemo(() => {
    if (!sortState) return rows;
    const acc = accessors[sortState.key];
    if (!acc) return rows;
    const dir = sortState.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last regardless of direction
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    // accessors is a stable per-render literal; sort only needs to recompute on rows/sortState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortState]);
  return { sorted, sortState, toggle };
}

export function HeadCell({
  labelId,
  defaultLabel,
  label,
  sortable,
  sortKey,
  sortState,
  onSort,
  className,
}: {
  labelId?: FormatjsIntl.Message["ids"];
  defaultLabel?: string;
  label?: React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
  sortState?: SortState;
  onSort?: (key: string) => void;
  className?: string;
}) {
  const content = label ?? (labelId ? <FormattedMessage id={labelId} defaultMessage={defaultLabel} /> : null);
  const active = Boolean(sortKey && sortState?.key === sortKey);
  const caret = sortable ? (
    <ChevronDown className={cn("tw-size-4 tw-transition-transform", active ? "tw-opacity-100" : "tw-opacity-50", active && sortState?.direction === "asc" && "tw-rotate-180")} />
  ) : null;

  if (sortable && sortKey && onSort) {
    return (
      <th
        aria-sort={active ? (sortState?.direction === "asc" ? "ascending" : "descending") : "none"}
        className={cn("tw-px-3 tw-py-2 tw-text-left tw-align-middle tw-font-normal tw-text-muted-foreground", className)}
      >
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-font-normal tw-text-muted-foreground hover:tw-text-foreground",
            active && "tw-text-foreground",
          )}
        >
          {content}
          {caret}
        </button>
      </th>
    );
  }

  return (
    <th className={cn("tw-px-3 tw-py-2 tw-text-left tw-align-middle tw-font-normal tw-text-muted-foreground", className)}>
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        {content}
        {caret}
      </span>
    </th>
  );
}
